import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { User, Lock, Building2, ScrollText, DatabaseBackup, Loader2, Download, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message);

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";

  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [company, setCompany] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState("");
  const restoreRef = useRef(null);

  useEffect(() => {
    api.get("/settings/profile").then(({ data }) => setProfile({ name: data.name || "", email: data.email || "" })).catch(err);
    if (isAdmin) api.get("/settings/company").then(({ data }) => setCompany(data)).catch(err);
    api.get("/settings/activity").then(({ data }) => setLogs(data.logs || [])).catch(() => {});
  }, [isAdmin]);

  const saveProfile = async () => {
    setBusy("profile");
    try { await api.put("/settings/profile", profile); toast.success("Profile updated"); }
    catch (e) { err(e); } finally { setBusy(""); }
  };

  const savePassword = async () => {
    if (pwd.new_password !== pwd.confirm) return toast.error("New passwords do not match");
    setBusy("pwd");
    try {
      await api.put("/settings/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const saveCompany = async () => {
    setBusy("company");
    try { const { data } = await api.put("/settings/company", company); setCompany(data); toast.success("Settings saved"); }
    catch (e) { err(e); } finally { setBusy(""); }
  };

  const downloadBackup = async () => {
    setBusy("backup");
    try {
      const { data } = await api.get("/settings/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `streamcore-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const doRestore = async (file) => {
    if (!file) return;
    if (!window.confirm("Restore will REPLACE all current data with the backup. Continue?")) return;
    setBusy("restore");
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await api.post("/settings/restore", json);
      toast.success(`Restored ${Object.values(data.restored).reduce((a, b) => a + b, 0)} records`);
    } catch (e) { err(e); } finally { setBusy(""); if (restoreRef.current) restoreRef.current.value = ""; }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader overline="Configuration" title="Settings" description="Manage your account, company and system data." />
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="h-4 w-4 mr-1.5" /> Profile</TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password"><Lock className="h-4 w-4 mr-1.5" /> Password</TabsTrigger>
          {isAdmin && <TabsTrigger value="company" data-testid="tab-company"><Building2 className="h-4 w-4 mr-1.5" /> Company</TabsTrigger>}
          <TabsTrigger value="activity" data-testid="tab-activity"><ScrollText className="h-4 w-4 mr-1.5" /> Activity</TabsTrigger>
          {isAdmin && <TabsTrigger value="backup" data-testid="tab-backup"><DatabaseBackup className="h-4 w-4 mr-1.5" /> Backup</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile">
          <Card className="p-6 max-w-lg space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} data-testid="profile-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} data-testid="profile-email" /></div>
            <Button onClick={saveProfile} disabled={busy === "profile"} data-testid="save-profile">{busy === "profile" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save profile</Button>
          </Card>
        </TabsContent>

        <TabsContent value="password">
          <Card className="p-6 max-w-lg space-y-4">
            <div className="space-y-2"><Label>Current password</Label><Input type="password" value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} data-testid="pwd-current" /></div>
            <div className="space-y-2"><Label>New password</Label><Input type="password" value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} data-testid="pwd-new" /></div>
            <div className="space-y-2"><Label>Confirm new password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} data-testid="pwd-confirm" /></div>
            <Button onClick={savePassword} disabled={busy === "pwd"} data-testid="save-password">{busy === "pwd" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Change password</Button>
          </Card>
        </TabsContent>

        {isAdmin && company && (
          <TabsContent value="company">
            <Card className="p-6 max-w-lg space-y-4">
              <div className="space-y-2"><Label>Company name</Label><Input value={company.company_name || ""} onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))} data-testid="company-name" /></div>
              <div className="space-y-2"><Label>Support contact</Label><Input value={company.support_contact || ""} onChange={(e) => setCompany((c) => ({ ...c, support_contact: e.target.value }))} placeholder="email / telegram / whatsapp" data-testid="company-support" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Currency</Label><Input value={company.currency || ""} onChange={(e) => setCompany((c) => ({ ...c, currency: e.target.value }))} data-testid="company-currency" /></div>
                <div className="space-y-2"><Label>Default test hours</Label><Input type="number" value={company.default_test_duration ?? ""} onChange={(e) => setCompany((c) => ({ ...c, default_test_duration: Number(e.target.value) }))} data-testid="company-testdur" /></div>
              </div>
              <div className="space-y-2">
                <Label>Minimum reseller credits required to create a Test</Label>
                <Input type="number" min="0" value={company.min_test_credits ?? ""} onChange={(e) => setCompany((c) => ({ ...c, min_test_credits: Number(e.target.value) }))} data-testid="company-min-test-credits" />
                <p className="text-xs text-muted-foreground">Resellers below this balance cannot create test accounts. Default: 10.</p>
              </div>
              <div className="space-y-2"><Label>Logo URL</Label><Input value={company.logo_url || ""} onChange={(e) => setCompany((c) => ({ ...c, logo_url: e.target.value }))} data-testid="company-logo" /></div>
              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="cursor-pointer">Test accounts enabled</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">When off, resellers cannot create any test accounts.</p>
                  </div>
                  <Switch checked={company.tests_enabled !== false} onCheckedChange={(v) => setCompany((c) => ({ ...c, tests_enabled: v }))} data-testid="company-tests-enabled" />
                </div>
                {company.tests_enabled === false && (
                  <div className="space-y-2">
                    <Label>Message shown when tests are disabled</Label>
                    <Textarea rows={2} value={company.tests_disabled_message || ""} onChange={(e) => setCompany((c) => ({ ...c, tests_disabled_message: e.target.value }))} placeholder="Tests are temporarily disabled. Please contact support." data-testid="company-tests-disabled-msg" />
                  </div>
                )}
              </div>
              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="cursor-pointer">Subscription expiry notifications</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Shows an info channel at the top of the customer's list (M3U & Xtream) before and after expiry.</p>
                  </div>
                  <Switch checked={company.notify_enabled !== false} onCheckedChange={(v) => setCompany((c) => ({ ...c, notify_enabled: v }))} data-testid="company-notify-enabled" />
                </div>
                {company.notify_enabled !== false && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Start reminding this many days before expiry</Label>
                      <Input type="number" min="0" value={company.notify_before_days ?? ""} onChange={(e) => setCompany((c) => ({ ...c, notify_before_days: Number(e.target.value) }))} data-testid="company-notify-days" />
                      <p className="text-xs text-muted-foreground">Default 7. The reminder counts down each day (7, 3, 0…) until expiry.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Reminder message (before expiry)</Label>
                      <Textarea rows={2} value={company.notify_msg_before || ""} onChange={(e) => setCompany((c) => ({ ...c, notify_msg_before: e.target.value }))} placeholder="Your subscription will expire in {days} days. Please contact your provider." data-testid="company-notify-before" />
                      <p className="text-xs text-muted-foreground">Use <code>{"{days}"}</code> for the remaining days.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Message after expiry</Label>
                      <Textarea rows={2} value={company.notify_msg_expired || ""} onChange={(e) => setCompany((c) => ({ ...c, notify_msg_expired: e.target.value }))} placeholder="Your subscription has expired. Please contact your provider to renew your subscription." data-testid="company-notify-expired" />
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={saveCompany} disabled={busy === "company"} data-testid="save-company">{busy === "company" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save settings</Button>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="activity">
          <Card className="border-border overflow-hidden">
            {logs.length === 0 ? (
              <p className="p-12 text-center text-sm text-muted-foreground">No activity recorded yet.</p>
            ) : (
              <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
                {logs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between px-4 py-3" data-testid={`activity-${l.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{l.action}</Badge>
                        <span className="text-sm truncate">{l.detail}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{l.actor_name} · {l.actor_role}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-3">{l.created_at ? format(new Date(l.created_at), "dd-MM-yyyy HH:mm") : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="backup">
            <Card className="p-6 max-w-lg space-y-4">
              <div>
                <h3 className="font-medium">Backup</h3>
                <p className="text-sm text-muted-foreground mb-3">Download a JSON snapshot of your configuration and data (excludes bulky playlist chunks — re-scan links after a restore).</p>
                <Button variant="outline" onClick={downloadBackup} disabled={busy === "backup"} data-testid="download-backup">{busy === "backup" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download backup</Button>
              </div>
              <div className="border-t border-border pt-4">
                <h3 className="font-medium text-destructive">Restore</h3>
                <p className="text-sm text-muted-foreground mb-3">Replace ALL current data with a backup file. This cannot be undone.</p>
                <input ref={restoreRef} type="file" accept="application/json" className="hidden" onChange={(e) => doRestore(e.target.files?.[0])} data-testid="restore-input" />
                <Button variant="outline" className="text-destructive" onClick={() => restoreRef.current?.click()} disabled={busy === "restore"} data-testid="restore-button">{busy === "restore" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Restore from file</Button>
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
